import { addToCart } from "./add-to-cart";
import { formatMoney } from "./g-utils";

class IntelligentBooksellerSuggestionItem extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Initialize element references (called each time element is connected to DOM)
    this.intelligentBookseller = this.closest("intelligent-bookseller");
    this.productHandle = this.dataset.productHandle;
    this.productForm = this.querySelector(".js-product-form");
    this.submitButton = this.productForm?.querySelector('[data-suggestion-item-add-btn]');
    this.addTextElement = this.querySelector('[data-suggestion-item-add-btn-text]');
    this.loaderElement = this.querySelector('[data-suggestion-item-add-btn-loader]');
    this.removeButton = this.querySelector('[data-suggestion-item-remove]');
    this.switchButton = this.querySelector('[data-suggestion-item-switch]');

    // Attach event listeners only if not already attached
    if (!this._listenersAttached) {
      this._attachEventListeners();
      this._listenersAttached = true;
    }
  }

  disconnectedCallback() {
    // Remove event listeners to prevent memory leaks
    if (this.productForm && this._boundOnFormSubmit) {
      this.productForm.removeEventListener("submit", this._boundOnFormSubmit);
      this._boundOnFormSubmit = null;
    }
    if (this.removeButton && this._boundOnRemoveClick) {
      this.removeButton.removeEventListener("click", this._boundOnRemoveClick);
      this._boundOnRemoveClick = null;
    }
    if (this.switchButton && this._boundOnSwitchClick) {
      this.switchButton.removeEventListener("click", this._boundOnSwitchClick);
      this._boundOnSwitchClick = null;
    }
    
    // Reset the listeners attached flag
    this._listenersAttached = false;
  }

  _attachEventListeners() {
    if (this.productForm) {
      // Store the bound handler to be able to remove it later
      this._boundOnFormSubmit = this._onFormSubmit.bind(this);
      this.productForm.addEventListener("submit", this._boundOnFormSubmit);
    }
    if (this.removeButton) {
      // Store the bound handler to be able to remove it later
      this._boundOnRemoveClick = this._onRemoveClick.bind(this);
      this.removeButton.addEventListener("click", this._boundOnRemoveClick);
    }
    if (this.switchButton) {
      // Store the bound handler to be able to remove it later
      this._boundOnSwitchClick = this._onSwitchClick.bind(this);
      this.switchButton.addEventListener("click", this._boundOnSwitchClick);
    }
  }

  _showSuccess(successType) {
    const successElement = this.querySelector(`[data-success-${successType}]`);
    if (successElement) {
      successElement.removeAttribute("aria-hidden");
    }
  }

  _hideAllErrors() {
    const errorMessages = [
      this.querySelector('[data-error-add-to-cart]'),
      this.querySelector('[data-error-switch-no-items]'),
      this.querySelector('[data-error-switch-general]'),
      this.querySelector('[data-error-remove]')
    ];
    
    errorMessages.forEach(errorMsg => {
      if (errorMsg) {
        errorMsg.setAttribute("aria-hidden", "true");
      }
    });
  }

  _showError(errorType) {
    this._hideAllErrors();
    const errorElement = this.querySelector(`[data-error-${errorType}]`);
    if (errorElement) {
      errorElement.removeAttribute("aria-hidden");
    }
  }

  async _onFormSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    // Start loading
    if (this.addTextElement) {
      this.addTextElement.setAttribute("aria-hidden", "true");
    }
    if (this.loaderElement) {
      this.loaderElement.setAttribute("aria-hidden", "false");
    }
    if (this.submitButton) {
      this.submitButton.setAttribute("aria-disabled", "true");
    }
    this._hideAllErrors();

    const properties = {
      Auteur: this.productForm["properties[Auteur]"].value,
      _collectionTitle: this.productForm["properties[_collectionTitle]"].value,
      _collectionId: this.productForm["properties[_collectionId]"].value,
      _from_intelligent_bookseller: true,
      _addedFrom: 'magic_cart',
    };

    try {
      const json = await addToCart(
        [{ id: this.selectedVariantId, quantity: 1, properties }],
        {
          klaviyoItems: [this.klaviyoAtcEventData],
          eventData: this.atcEventData,
        }
      );

      if (json) {
        this._showSuccess("add-to-cart");
      } else {
        throw new Error("Failed to add item to cart");
      }
    } catch (error) {
      console.error("Error:", error);
      this._showError("add-to-cart");
    } finally {
      if (this.addTextElement) {
        this.addTextElement.setAttribute("aria-hidden", "false");
      }
      if (this.loaderElement) {
        this.loaderElement.setAttribute("aria-hidden", "true");
      }
      if (this.submitButton) {
        this.submitButton.setAttribute("aria-disabled", "false");
      }
    }
  }

  async _onSwitchClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if(!this.intelligentBookseller || !this.productHandle) return;

    // Start loading
    this.switchButton.setAttribute("aria-disabled", "true");
    this._hideAllErrors();

    // Switch item
    try {
      await this.intelligentBookseller.switchItem(this.productHandle);
    } catch (error) {
      console.error("Error:", error);
      // Check if it's the "no items available" error
      if (error.message && error.message.includes("No available items to replace with")) {
        this._showError("switch-no-items");
      } else {
        this._showError("switch-general");
      }
    } finally {
      // Stop loading
      this.switchButton.setAttribute("aria-disabled", "false");
    }
  }

  _onRemoveClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if(!this.intelligentBookseller || !this.productHandle) return;

    // Start loading
    this.removeButton.setAttribute("aria-disabled", "true");
    this._hideAllErrors();

    try {
      this.intelligentBookseller.removeItem(this.productHandle);
    } catch (error) {
      console.error("Error removing item:", error);
      this._showError("remove");
    } finally {
      // Stop loading
      this.removeButton.setAttribute("aria-disabled", "false");
    }
  }

  get selectedVariantId() {
    return this.productForm["variant-id"].value;
  }

  get selectedVariant() {
    return this.productJson.variants[this.selectedVariantId];
  }

  get productJson() {
    if (!this._productJson) {
      this._productJson = JSON.parse(this.querySelector(".js-product-json").innerHTML);
    }
    return this._productJson;
  }

  get atcEventData() {
    if (!this._initialAtcData) {
      this._initialAtcData = JSON.parse(this.querySelector(".js-atc-event-data").innerHTML);
    }
    const newAtcEventData = { ...this._initialAtcData };
    newAtcEventData.cloud_retail.visitorId = localStorage.getItem("visitorId");
    newAtcEventData.cloud_retail.productDetails = [
      {
        product: {
          id: this.selectedVariant.barcode,
        },
        quantity: 1
      },
    ];
    newAtcEventData.cloud_retail.cartId = window.cartToken;
    newAtcEventData.cloud_retail.userInfo = { userId: window.userId };
    newAtcEventData.productDetails[0].product.id = this.selectedVariant.barcode;

    return newAtcEventData;
  }

  get klaviyoAtcEventData() {
    if(!this._initialKlaviyoAtcEventData) {
      this._initialKlaviyoAtcEventData = JSON.parse(this.querySelector(".js-klaviyo-atc-event-data").innerHTML);
    }

    const _klaviyoAtcEventData = {...this._initialKlaviyoAtcEventData};
    _klaviyoAtcEventData.Price = this.selectedVariant ? formatMoney(this.selectedVariant.price) : null;

    return _klaviyoAtcEventData;
  }
}

if (!customElements.get("intelligent-bookseller-suggestion-item")) {
  customElements.define("intelligent-bookseller-suggestion-item", IntelligentBooksellerSuggestionItem);
}
